/**
 * v1 → v2 migration — re-encrypt plaintext rows that pre-date encryption.
 *
 * Runs OUTSIDE Dexie's `.upgrade()` callback (see `./schema.ts` for the
 * "why"). Triggered after auth bootstrap by `./security-lifecycle.ts`:
 *   - On Amplify `signedIn` events.
 *   - At install time, if `getCurrentUser()` resolves (covers cold-boot
 *     into an existing session).
 *
 * Properties:
 *   - **Idempotent.** Sets a localStorage flag on success; re-runs become
 *     no-ops. Safe to call from multiple triggers.
 *   - **Resumable.** Skips rows that already have `encrypted_data`, so a
 *     partial run that's interrupted (network drop mid-pass) finishes on
 *     the next trigger.
 *   - **Per-row failures don't block other rows.** A row that fails to
 *     encrypt (e.g. transient KMS unavailability mid-run) is left as v1;
 *     the next pass picks it up. The flag is only set when failed === 0.
 *   - **Coalesced concurrency.** A second concurrent caller awaits the
 *     first run rather than triggering a parallel pass.
 *
 * The encryption-bridge unwraps tolerate v1 rows during the transition
 * window, so reads and writes continue to work while the migration runs.
 * Writes always go through `wrapForStorage` / `wrapSyncQueuePayload`, so
 * any new row is born encrypted — the migration only chases legacy rows.
 */

import { db } from "./schema";
import {
  wrapForStorage,
  wrapSyncQueuePayload,
} from "./encryption-bridge";
import type { EntityType, SyncQueueRecord } from "./db.types";
import { logger } from "../utils/logger";

const MIGRATION_FLAG_KEY = "kaagazy_v2_migration_done";

interface EntityTableSpec {
  table:  "patients" | "visits" | "vitals" | "prescriptions" | "appointments" | "allergies";
  entity: EntityType;
  pk:     string;
}

const ENTITY_TABLES: readonly EntityTableSpec[] = [
  { table: "patients",      entity: "patient",      pk: "patient_id" },
  { table: "visits",        entity: "visit",        pk: "visit_id" },
  { table: "vitals",        entity: "vitals",       pk: "vital_id" },
  { table: "prescriptions", entity: "prescription", pk: "prescription_id" },
  { table: "appointments",  entity: "appointment",  pk: "appointment_id" },
  { table: "allergies",     entity: "allergy",      pk: "allergy_id" },
] as const;

export interface MigrationResult {
  migrated:    number;
  skipped:     number;
  failed:      number;
  alreadyDone: boolean;
}

let inFlight: Promise<MigrationResult> | null = null;

/** True if this device has previously completed v2 migration. */
export function isV2MigrationDone(): boolean {
  try {
    return localStorage.getItem(MIGRATION_FLAG_KEY) === "true";
  } catch {
    // localStorage may be unavailable in private mode / sandboxed iframes.
    return false;
  }
}

/**
 * Run the v1 → v2 re-encrypt pass. Concurrent callers share the same
 * in-flight promise. Idempotent: a second call after success returns
 * `{ alreadyDone: true }` without touching the DB.
 */
export function runV2Migration(): Promise<MigrationResult> {
  if (inFlight) return inFlight;

  if (isV2MigrationDone()) {
    return Promise.resolve({ migrated: 0, skipped: 0, failed: 0, alreadyDone: true });
  }

  inFlight = doMigrate().finally(() => { inFlight = null; });
  return inFlight;
}

async function doMigrate(): Promise<MigrationResult> {
  let migrated = 0;
  let skipped  = 0;
  let failed   = 0;

  for (const spec of ENTITY_TABLES) {
    const out = await migrateEntityTable(spec);
    migrated += out.migrated;
    skipped  += out.skipped;
    failed   += out.failed;
  }

  const queueOut = await migrateSyncQueue();
  migrated += queueOut.migrated;
  skipped  += queueOut.skipped;
  failed   += queueOut.failed;

  if (failed === 0) {
    try {
      localStorage.setItem(MIGRATION_FLAG_KEY, "true");
    } catch {
      // localStorage unavailable; the pass still ran cleanly, just won't be
      // skipped next time. Acceptable.
    }
    logger.warn("v2-migration complete", { migrated, skipped });
  } else {
    // Don't mark complete — the next trigger picks up the failed rows.
    logger.warn("v2-migration partial", { migrated, skipped, failed });
  }

  return { migrated, skipped, failed, alreadyDone: false };
}

interface PassResult { migrated: number; skipped: number; failed: number; }

async function migrateEntityTable(spec: EntityTableSpec): Promise<PassResult> {
  const out: PassResult = { migrated: 0, skipped: 0, failed: 0 };
  const table = db[spec.table] as unknown as {
    toArray: () => Promise<unknown[]>;
    get:     (key: string) => Promise<unknown>;
    put:     (value: unknown) => Promise<unknown>;
  };
  const rows = await table.toArray();

  for (const row of rows) {
    const r = row as Record<string, unknown>;
    if (rowAlreadyEncrypted(r)) {
      out.skipped += 1;
      continue;
    }

    const id = r[spec.pk] as string | undefined;
    if (typeof id !== "string" || id.length === 0) {
      // Malformed row (no primary key) — can't safely write back without a
      // CAS check. Skip and log; the next pass will revisit if the row
      // somehow gets a PK.
      out.skipped += 1;
      logger.warn("v2-migration entity row missing pk", { entity: spec.entity });
      continue;
    }

    let envelope: Record<string, unknown>;
    try {
      // Crypto runs OUTSIDE the Dexie transaction below — Web Crypto promises
      // don't participate in Dexie's transaction lifecycle.
      envelope = await wrapForStorage(spec.entity, r);
    } catch (err) {
      out.failed += 1;
      logger.error("v2-migration entity row failed", err instanceof Error ? err : undefined, { entity: spec.entity });
      continue;
    }

    // Concurrent-write protection: re-fetch immediately before put. If
    // `operations.ts` (or any other writer) has already encrypted the same
    // row in the meantime, skip — overwriting the concurrent writer's
    // envelope with our re-encrypt of the stale plaintext would be silent
    // data loss.
    //
    // There's a small race window between this `get` and the `put` that a
    // true atomic CAS would close. Closing it would require a Dexie
    // transaction wrapping both calls; awaiting Web Crypto inside such a
    // transaction risks the auto-commit problem documented in `schema.ts`.
    // Pre-prod the window is acceptable (no concurrent writers exist yet);
    // a fully-atomic version is tracked as a hard prerequisite before any
    // service starts writing PHI to Dexie.
    try {
      const current = await table.get(id) as Record<string, unknown> | undefined;
      if (!current) {
        // Row was deleted between toArray and re-fetch — neutral outcome.
        continue;
      }
      if (rowAlreadyEncrypted(current)) {
        out.skipped += 1;
        continue;
      }
      await table.put(envelope);
      out.migrated += 1;
    } catch (err) {
      out.failed += 1;
      logger.error("v2-migration entity put failed", err instanceof Error ? err : undefined, { entity: spec.entity });
    }
  }

  return out;
}

async function migrateSyncQueue(): Promise<PassResult> {
  const out: PassResult = { migrated: 0, skipped: 0, failed: 0 };
  const rows = await db.sync_queue.toArray();

  for (const row of rows) {
    const r = row as unknown as Record<string, unknown>;
    if (rowAlreadyEncrypted(r)) {
      out.skipped += 1;
      continue;
    }
    if (typeof r.data === "undefined") {
      // Malformed row (no `data`, no `encrypted_data`) — skip + log.
      logger.warn("v2-migration sync_queue malformed");
      out.skipped += 1;
      continue;
    }

    const id = r.id as string | undefined;
    if (typeof id !== "string" || id.length === 0) {
      out.skipped += 1;
      logger.warn("v2-migration sync_queue row missing id");
      continue;
    }

    let envelope: Record<string, unknown>;
    try {
      envelope = await wrapSyncQueuePayload(r as unknown as SyncQueueRecord);
    } catch (err) {
      out.failed += 1;
      logger.error("v2-migration sync_queue row failed", err instanceof Error ? err : undefined);
      continue;
    }

    // Same concurrent-write protection as the entity tables: re-fetch
    // before put. See migrateEntityTable for the documented race window.
    try {
      const current = await db.sync_queue.get(id) as Record<string, unknown> | undefined;
      if (!current) {
        continue; // row pruned between toArray and re-fetch — neutral.
      }
      if (rowAlreadyEncrypted(current)) {
        out.skipped += 1;
        continue;
      }
      await db.sync_queue.put(envelope as unknown as Parameters<typeof db.sync_queue.put>[0]);
      out.migrated += 1;
    } catch (err) {
      out.failed += 1;
      logger.error("v2-migration sync_queue put failed", err instanceof Error ? err : undefined);
    }
  }

  return out;
}

function rowAlreadyEncrypted(row: Record<string, unknown>): boolean {
  return typeof row.encrypted_data === "string" && (row.encrypted_data as string).length > 0;
}

/** Test-only: reset the completion flag so a fresh migration can be run. */
export function _resetMigrationFlag(): void {
  try {
    localStorage.removeItem(MIGRATION_FLAG_KEY);
  } catch {
    // localStorage unavailable; nothing to reset.
  }
}
